import { getAppConfigSingleton } from "@expert/app-config";
import { useNotification } from "@expert/common-ui";
import { useFeatures, type WorkspaceFeatures } from "@expert/features";
import { useCallback, useState } from "react";
import { shallow } from "zustand/shallow";

import { useVerifiedCustomerStore } from "@/customer-info";
import { useTaskMdn } from "@/customer-panel/hooks/useTaskMdn";
import { usePartner, useSession } from "@/sdk";
import { useLogger } from "@/sdk/logging/LoggerProvider";
import { getHashedValue, removeCountryCode } from "@/shared-utils";

import { getBackupGaiaSummary, getGaiaSummary } from "../api/sessionSummaryApiClient";
import { useSessionSummaryStore } from "../store/useSessionSummaryStore";
import { getPartnerTool } from "../utils";
import { useSessionSummaryDispatchActions } from "./hooks";
import { defaultAdditionalSummaryTimes } from "./utils";

const EMPTY_CUSTOMER: { accountNumber?: string } = {};

/**
 * Hook for session summary logic to separate from UI components
 */
export const useSummary = () => {
    const logger = useLogger({ module: "sessionSummary" });
    const appConfig = getAppConfigSingleton();
    const activeSession = useSession();
    const partner = usePartner();
    const notification = useNotification();

    const [summaryGenerated, setSummaryGenerated] = useState(false);

    const { summary, submitted, setSessionSummary } = useSessionSummaryStore((state) => state, shallow);
    const { mdn: verifiedMdn, accountNumber } = useVerifiedCustomerStore(
        (state) => state.getVerifiedCustomer(activeSession.id).customer || {},
    );

    const validAccountNumber =
        accountNumber !== undefined &&
        (appConfig.getPartnersConfig(partner).allowedAccountNumberLengths?.includes(accountNumber.length) ??
            EMPTY_CUSTOMER);

    const {
        requiresManualCopy,
        gaiaSummaryModel,
        sessionSummaryTimes = defaultAdditionalSummaryTimes,
    } = appConfig.getPartnersConfig(partner);

    const shouldSendSummary = !submitted && summary;

    const {
        features: { sendSummaryFromBackend },
    } = useFeatures<WorkspaceFeatures>();
    const { dispatchSessionSubmit, dispatchSessionSummaryStats } = useSessionSummaryDispatchActions();
    const { resolutionStatus, category, subcategory } = useSessionSummaryStore();
    const [setSubmitted] = useSessionSummaryStore((state) => [state.setSubmitted, state.autogeneratedSummary]);
    const taskMdn = useTaskMdn({
        blocklistedNumbers: undefined,
        blocklistedPrefixes: undefined,
    });
    const mdn = taskMdn || verifiedMdn || "";
    const extraSessionSummaryPayloadProps = appConfig.getPartnersConfig(partner).extraSessionSummaryPayloadProps;

    /** Dispatch analytics with post-call summary to EIP */
    const sendSessionSummaryToEIP = useCallback(
        async (summaryText: string, agentCallSid: string) => {
            const mdnFormatted = removeCountryCode(mdn);
            const mdnHashed = await getHashedValue(mdnFormatted);

            const sessionSummaryPayload = {
                accountNo: accountNumber,
                remarkText: summaryText,
                resolutionStatus,
                ...extraSessionSummaryPayloadProps,
                ...(category ? { category } : {}),
                ...(subcategory ? { subcategory } : {}),
            };

            try {
                if (!sendSummaryFromBackend) {
                    dispatchSessionSubmit({ agentCallSid, mdn: mdnFormatted, mdnHashed, sessionSummaryPayload });
                }
                setSubmitted(true);
                notification.success("Summary sent");
            } catch (error) {
                notification.error(
                    `Failed to send session summary! Copy notes manually to ${getPartnerTool(partner)}.`,
                );
                logger.warn({ error }, "Failed to send session summary!");
            }

            // dispatch session summary stats without PII so we have an analytic in OpenSearch
            dispatchSessionSummaryStats({
                agentCallSid,
                autogeneratedSummary: summaryText,
                expirationTimestamp: 0,
                gaiaSummaryModel,
                summaryWasCopiedToClipboard: false,
                summaryWasSubmittedViaPartnerTool: undefined,
                timeRanOut: false,
                update: summaryText,
            });
        },
        [
            accountNumber,
            category,
            dispatchSessionSubmit,
            dispatchSessionSummaryStats,
            extraSessionSummaryPayloadProps,
            gaiaSummaryModel,
            logger,
            mdn,
            notification,
            partner,
            resolutionStatus,
            sendSummaryFromBackend,
            setSubmitted,
            subcategory,
        ],
    );

    const setSessionSummaryFromGaia = useCallback(
        async (setSummaryLoaded: (arg0: boolean) => void, agentCallSid?: string) => {
            const logIdentities = { agentCallSid, sessionId: activeSession.id, partner };
            logger.info(logIdentities, "Gaia summary fetch check");

            const generatedSummary = agentCallSid
                ? await getGaiaSummary(
                      logger,
                      { callSid: agentCallSid, sessionId: activeSession.id, partner },
                      gaiaSummaryModel,
                  )
                : await getBackupGaiaSummary(logger, { sessionId: activeSession.id, partner }, gaiaSummaryModel);

            if (!generatedSummary) {
                logger.warn(logIdentities, "Received empty summary from GAIA.");
            } else {
                logger.info("Received non-empty summary from GAIA.");
                setSessionSummary({ value: generatedSummary, autoGenerated: true });
                setSummaryGenerated(!!generatedSummary);
                sendSessionSummaryToEIP(generatedSummary, agentCallSid ?? "");
                logger.info(logIdentities, "Sent GAIA session summary to analytics service.");
            }
            setSummaryLoaded(true);
        },
        [activeSession.id, logger, partner, setSessionSummary, gaiaSummaryModel, sendSessionSummaryToEIP],
    );

    const onDismiss = useCallback(
        async (closeModal: () => void, _agentCallSid?: string, { checkSummary = true } = {}) => {
            logger.trace("Session summary dismissed");
            closeModal();
            if (checkSummary && shouldSendSummary) {
                notification.success("Session summary copied to clipboard");
                try {
                    await navigator.clipboard.writeText(summary);
                } catch (error) {
                    logger.warn({ error }, "Session summary not copied to clipboard");
                }
            }
        },
        [logger, shouldSendSummary, summary, notification],
    );

    const getExtraSummaryTime = useCallback(
        (canAutosend: boolean, hasSummaryOnly: boolean) => {
            if (canAutosend) {
                return sessionSummaryTimes.autosend;
            } else if (hasSummaryOnly) {
                return sessionSummaryTimes.manualCopy;
            }
            return sessionSummaryTimes.maximum;
        },
        [sessionSummaryTimes],
    );

    return {
        summaryGenerated,
        validAccountNumber,
        requiresManualCopy,
        sessionSummaryTimes,
        summary,
        setSessionSummaryFromGaia,
        onDismiss,
        getExtraSummaryTime,
    };
};
